import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { KeyRound, Pencil } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { AccountBand, AccountSections } from "@/components/account-details";
import { AvatarPicker } from "@/components/avatar-picker";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Page, PageHeader } from "@/components/ui/page";
import { Field, Input } from "@/components/ui/field";
import { errorMessage } from "@/lib/api";
import { profileQuery, useUpdateProfile } from "@/lib/queries";
import { useTranslation } from "@/stores/locale";

export const Route = createFileRoute("/_app/profile")({ component: ProfilePage });

const schema = z.object({
  first_name: z.string().trim().min(1).max(100),
  middle_name: z.string().trim().max(100),
  last_name: z.string().trim().min(1).max(100),
  email_address: z.string().trim().email().or(z.literal("")),
});

type ProfileInput = z.infer<typeof schema>;

/**
 * The account as it stands, read first; the three things a person may do to it
 * sit on top. Editing swaps the details for the form in place, so the page
 * never loses its shape.
 */
function ProfilePage() {
  const t = useTranslation();
  const { data: profile } = useQuery(profileQuery());
  const update = useUpdateProfile();
  const [editing, setEditing] = useState(false);

  const form = useForm<ProfileInput>({
    resolver: zodResolver(schema),
    values: {
      first_name: profile?.first_name ?? "",
      middle_name: profile?.middle_name ?? "",
      last_name: profile?.last_name ?? "",
      email_address: profile?.email_address ?? "",
    },
  });
  const errors = form.formState.errors;

  if (!profile) return null;
  const name = profile.full_name || profile.user_name;

  const setPicture = async (avatar: string) => {
    try {
      await update.mutateAsync({ avatar });
      toast.success(t("shell.pictureUpdated"));
    } catch (error) {
      toast.error(errorMessage(error, t("shell.pictureFailed")));
    }
  };

  const submit = form.handleSubmit((values) =>
    update.mutate(
      {
        first_name: values.first_name,
        middle_name: values.middle_name || null,
        last_name: values.last_name,
        ...(values.email_address ? { email_address: values.email_address } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("profile.updated"));
          setEditing(false);
        },
        onError: (error) => toast.error(errorMessage(error, t("profile.saveFailed"))),
      },
    ),
  );

  return (
    <Page>
      <PageHeader title={t("page.profile")} description={t("profile.subtitle")} />

      {/* One card, top to bottom: who they are, then everything on the account. */}
      <Card>
        <AccountBand
          account={profile}
          avatar={
            // The same picker the employee record uses, so a picture is
            // changed the one way wherever it is changed — including from the
            // camera, which this screen could not do at all.
            <AvatarPicker
              src={profile.avatar}
              name={name}
              busy={update.isPending}
              onChange={setPicture}
              onRemove={() =>
                update.mutate(
                  { avatar: "" },
                  { onSuccess: () => toast.success(t("profile.pictureRemoved")) },
                )
              }
            />
          }
          actions={
            <>
              <Button variant="outline" onClick={() => setEditing((open) => !open)}>
                <Pencil /> {editing ? t("profile.cancel") : t("profile.editProfile")}
              </Button>
              <Button asChild>
                <Link to="/profile/password">
                  <KeyRound /> {t("shell.changePassword")}
                </Link>
              </Button>
            </>
          }
        />

        {editing ? (
          <div className="border-line border-t p-6">
            <form className="grid max-w-2xl gap-5" onSubmit={submit} noValidate>
              <Field
                label={t("emp.firstName")}
                htmlFor="first_name"
                error={errors.first_name && t("profile.errorName")}
              >
                <Input
                  id="first_name"
                  autoComplete="given-name"
                  autoFocus
                  aria-invalid={Boolean(errors.first_name)}
                  {...form.register("first_name")}
                />
              </Field>
              <Field label={t("emp.middleName")} htmlFor="middle_name">
                <Input
                  id="middle_name"
                  autoComplete="additional-name"
                  {...form.register("middle_name")}
                />
              </Field>
              <Field
                label={t("emp.lastName")}
                htmlFor="last_name"
                error={errors.last_name && t("profile.errorName")}
              >
                <Input
                  id="last_name"
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.last_name)}
                  {...form.register("last_name")}
                />
              </Field>
              <Field
                label={t("profile.email")}
                htmlFor="email_address"
                error={errors.email_address && t("profile.errorEmail")}
                hint={t("profile.emailHint")}
              >
                <Input
                  id="email_address"
                  type="email"
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email_address)}
                  {...form.register("email_address")}
                />
              </Field>
              <Field
                label={t("profile.employeeId")}
                htmlFor="user_name"
                hint={t("profile.employeeIdHint")}
              >
                <Input id="user_name" value={profile.user_name} disabled readOnly />
              </Field>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                  {t("profile.cancel")}
                </Button>
                <Button
                  type="submit"
                  loading={update.isPending}
                  loadingLabel={t("profile.saving")}
                >
                  {t("profile.save")}
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <AccountSections account={profile} />
        )}
      </Card>
    </Page>
  );
}
